import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import med from "../../assets/images/logo.png";
import { useAppStore } from "../../store";
import { NAV_ITEMS, activeNavKey } from "./nav-items";

type Props = {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function Sidebar({ collapsed, setCollapsed }: Props) {
  const { doctor, logOut } = useAppStore();
  const location = useLocation();
  const activeKey = activeNavKey(location.pathname);

  const toggleCollapsed = () => setCollapsed(!collapsed);

  // `fullname` already carries the "Dr." title from the backend (the header
  // greeting renders it bare too) — don't prepend a second one.
  const displayName = doctor?.user?.fullname || "Doctor";
  const speciality = doctor?.speciality;
  const initials = displayName
    .replace(/^Dr\.?\s*/i, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0]?.toUpperCase())
    .join("");

  return (
    <div
      className={`hidden h-full shrink-0 flex-col border-r border-slate-100 bg-white lg:flex ${
        collapsed ? "w-[84px]" : "w-[248px]"
      }`}
      style={{ transition: "width 0.2s ease" }}
    >
      {/* Brand — logo.png is the full lockup (icon + wordmark baked into one
          image), so it's rendered as-is: no badge/background behind it, and
          no separate "Medpharma" text label, which would just duplicate the
          wordmark already in the image. */}
      <div
        className={`flex h-16 shrink-0 items-center ${collapsed ? "justify-center px-2" : "justify-between px-5"}`}
      >
        {collapsed ? (
          <img src={med} alt="Medpharma" className="h-7 w-auto max-w-9 object-contain" />
        ) : (
          <img src={med} alt="Medpharma" className="h-8 w-auto object-contain" />
        )}
        <button
          type="button"
          onClick={toggleCollapsed}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <MenuUnfoldOutlined className="text-lg" />
          ) : (
            <MenuFoldOutlined className="text-lg" />
          )}
        </button>
      </div>

      {/* Doctor identity */}
      <div
        className={`flex shrink-0 items-center gap-3 px-5 py-4 ${collapsed ? "justify-center px-2" : ""}`}
      >
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-[13px] font-semibold text-white"
          style={{ background: "linear-gradient(135deg, #00A2F8 0%, #0070C0 100%)" }}
        >
          {doctor?.profile_image ? (
            <img src={doctor.profile_image} alt="" className="h-full w-full object-cover" />
          ) : initials ? (
            initials
          ) : (
            <UserOutlined />
          )}
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="m-0 truncate text-[14px] font-semibold text-slate-800">
              {displayName}
            </p>
            {speciality && (
              <p className="m-0 truncate text-[12px] capitalize text-slate-400">
                {speciality}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mx-5 h-px shrink-0 bg-slate-100" />

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 pt-5">
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {NAV_ITEMS.map(({ key, label, path, icon }) => {
            const isActive = key === activeKey;
            return (
              <li key={key}>
                <NavLink
                  to={path}
                  title={collapsed ? label : undefined}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium transition-colors ${
                    collapsed ? "justify-center" : ""
                  } ${
                    isActive
                      ? "bg-[#EAF6FF] text-[#0070C0]"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                  }`}
                >
                  <span
                    className={`flex text-[18px] ${isActive ? "text-[#00A2F8]" : "text-slate-400"}`}
                  >
                    {icon}
                  </span>
                  {!collapsed && <span className="truncate">{label}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Help prompt — fills the natural empty space below a short nav with
          something useful instead of leaving it dead. */}
      {!collapsed && (
        <div className="mx-3 mb-3 shrink-0 rounded-2xl bg-slate-50 p-4">
          <div className="flex items-center gap-2 text-slate-700">
            <QuestionCircleOutlined className="text-[15px] text-[#00A2F8]" />
            <p className="m-0 text-[13px] font-semibold">Need help?</p>
          </div>
          <p className="mb-2.5 mt-1 text-[12px] leading-relaxed text-slate-500">
            Our team is here to support you.
          </p>
          <a
            href="mailto:customer@medpharma.care"
            className="text-[12px] font-semibold text-[#0070C0] hover:underline"
          >
            customer@medpharma.care
          </a>
        </div>
      )}

      <div className="mx-5 h-px shrink-0 bg-slate-100" />

      {/* Logout */}
      <div className="shrink-0 px-3 py-3">
        <button
          type="button"
          onClick={logOut}
          className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600 ${
            collapsed ? "justify-center" : ""
          }`}
          aria-label="Log out"
        >
          <LogoutOutlined className="text-[18px]" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}
